import * as React from 'react';
import { Repeat } from 'lucide-react';

import { cn } from '@/components/atoms/primitives/utils';

/**
 * The marker's long statement (DS 1.10.0 `reference/resale/cards.html`). The
 * visible word is always "Resale"; this rides `title` for hover.
 */
export const RESALE_MARKER_TITLE =
  "Resale listing. Tickets offered by resellers through Ticketmaster's resale marketplace";

export interface ResaleMarkerProps {
  /**
   * `sm` is the ≤720 Discover row variant (18px tall, 9px type). Callers whose
   * one element serves both widths keep `default` and let the surface's own
   * ≤720 rule shrink it.
   */
  size?: 'default' | 'sm';
  /** Element to render — `li` when the marker is an item of a fact list. */
  as?: 'span' | 'li';
  /** Omit the hover `title` where a sibling already states it. */
  withTitle?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * `ResaleMarker` — the DS 1.10.0 `.a-resale` atom (DES-98): an inline tag, not
 * a pill — mono 10px caps "Resale" + Lucide `repeat`, 4px radius, neutral
 * `--resale-*` ink. It marks a Ticketmaster resale listing beside its price;
 * it never joins the media badge stack and never carries a category ink.
 */
export function ResaleMarker({
  size = 'default',
  as: Element = 'span',
  withTitle = true,
  className,
  'data-testid': testId = 'resale-marker',
}: ResaleMarkerProps): React.ReactElement {
  return (
    <Element
      className={cn('a-resale', size === 'sm' && 'a-resale--sm', className)}
      title={withTitle ? RESALE_MARKER_TITLE : undefined}
      data-testid={testId}
    >
      <Repeat aria-hidden="true" />
      Resale
    </Element>
  );
}
